import type { StateCreator } from "zustand";

import { search, type SearchType } from "../../api/fetch.ts";
import type { CategoryResponse, ItemResponse, Tracks, TrackType } from "../../types.ts";
import { getGenomicFeatureTypeFromTrackType, getTrackTypeFromGenomicFeatureType } from "../../utils/type-conversion.ts";
import type { GenomeViewerStore } from "../store.ts";

export interface CategoryResult {
  category: CategoryResponse;
  matchingItems: ItemResponse[];
}

export interface GenomeResult {
  genomeId: number;
  genomeName: string;
  categories: CategoryResult[];
}

export interface SearchState {
  searchTerm: string;
  searchResults: Map<number, GenomeResult[]> | null;
  isSearchLoading: boolean;
  searchError: string | null;
  searchAbortController: AbortController | null;
}

export interface SearchActions {
  setSearchTerm: (term: string) => void;
  performSearch: (term: string) => Promise<void>;
  clearSearch: () => void;
  abortSearch: () => void;
}

export type SearchSlice = SearchActions & SearchState;

export const initialSearchState: SearchState = {
  searchTerm: "",
  searchResults: null,
  isSearchLoading: false,
  searchError: null,
  searchAbortController: null,
};

/**
 * Extracts unique SearchEntityTypeRequest objects from a tracks collection
 */
function getUniqueSearchEntityRequests(tracks: Tracks): SearchType[] {
  const typeMap = new Map<string, Set<number>>();

  for (const track of tracks.values()) {
    const searchType = getGenomicFeatureTypeFromTrackType(track.trackType);

    if (searchType) {
      if (!typeMap.has(searchType)) {
        typeMap.set(searchType, new Set());
      }

      if (track.subType?.id !== undefined) {
        typeMap.get(searchType)!.add(track.subType.id);
      }
    }
  }

  return Array.from(typeMap.entries()).map(([type, subTypeIdSet]) => ({
    type: type as SearchType["type"],
    subTypeIds: subTypeIdSet.size > 0 ? Array.from(subTypeIdSet).sort() : undefined,
  }));
}

export const createSearchSlice: StateCreator<GenomeViewerStore, [], [], SearchSlice> = (set, get) => ({
  ...initialSearchState,

  setSearchTerm: (term: string) => {
    set({ searchTerm: term });
  },

  performSearch: async (term: string) => {
    const state = get();
    const { syntenyOrganism, trackGroups, tracks, searchAbortController } = state;

    // Abort previous search if running
    if (searchAbortController) {
      searchAbortController.abort();
    }

    // Clear previous error
    set({ searchError: null });

    if (!syntenyOrganism.id || term.trim().length <= 2) {
      set({
        searchResults: null,
        isSearchLoading: false,
        searchAbortController: null,
      });
      return;
    }

    const abortController = new AbortController();
    set({
      isSearchLoading: true,
      searchAbortController: abortController,
    });

    try {
      const searchTypes = getUniqueSearchEntityRequests(tracks);
      const response = await search(syntenyOrganism.id, term.trim(), searchTypes, abortController.signal);

      if (abortController.signal.aborted) return;

      const genomeCategoryResponses = response?.categories ?? [];
      const doneGenomes = new Set<number>();
      const allResults = new Map<number, GenomeResult[]>();

      // Create a map for quick lookup of genome category responses
      const genomeCategoryMap = new Map(genomeCategoryResponses.map((gcr) => [gcr.genomeId, gcr]));

      // Create a map of which track types and subtypes are available for each genome
      const genomeTrackTypes = new Map<number, { types: Set<TrackType>; subtypes: Set<number> }>();
      for (const group of trackGroups.values()) {
        if (!genomeTrackTypes.has(group.organismId)) {
          genomeTrackTypes.set(group.organismId, { types: new Set(), subtypes: new Set() });
        }

        const trackInfo = genomeTrackTypes.get(group.organismId)!;

        // Get track types and subtypes for this genome by looking at tracks in this group
        for (const trackId of group.trackIds) {
          const track = tracks.get(trackId);
          if (track?.trackType) {
            trackInfo.types.add(track.trackType);

            // Add subtype if available
            if (track.subType?.id !== undefined) {
              trackInfo.subtypes.add(track.subType.id);
            }
          }
        }
      }

      // Process genomes in trackGroups order to maintain display order
      for (const group of trackGroups.values()) {
        if (doneGenomes.has(group.organismId)) continue;

        const genomeCategoryResponse = genomeCategoryMap.get(group.organismId);
        const availableTrackInfo = genomeTrackTypes.get(group.organismId);

        if (genomeCategoryResponse && genomeCategoryResponse.categoryResponses.length > 0 && availableTrackInfo) {
          // Filter categories to only include those with track types present for this genome
          const filteredCategories = genomeCategoryResponse.categoryResponses.filter((categoryResponse) => {
            // Convert genomic feature type to track type and check if available
            const expectedTrackType = getTrackTypeFromGenomicFeatureType(categoryResponse.categoryType);
            const hasMatchingTrackType = availableTrackInfo.types.has(expectedTrackType);

            if (!hasMatchingTrackType) return false;

            // If there's a subTypeId, check if it matches any available subtypes
            if (categoryResponse.subTypeId !== null && categoryResponse.subTypeId !== undefined) {
              return availableTrackInfo.subtypes.has(categoryResponse.subTypeId);
            }

            // If no subTypeId specified, track type match is sufficient
            return true;
          });

          if (filteredCategories.length > 0) {
            // Transform API response to our expected format
            const genomeResult: GenomeResult = {
              genomeId: genomeCategoryResponse.genomeId,
              genomeName: genomeCategoryResponse.genomeName,
              categories: filteredCategories.map((categoryResponse) => ({
                category: categoryResponse,
                matchingItems: categoryResponse.items || [],
              })),
            };

            allResults.set(genomeCategoryResponse.genomeId, [genomeResult]);
          }
        }

        doneGenomes.add(group.organismId);
      }

      if (!abortController.signal.aborted) {
        set({
          searchResults: allResults,
          isSearchLoading: false,
          searchAbortController: null,
        });
      }
    } catch {
      if (!abortController.signal.aborted) {
        set({
          searchError: "Failed to search.",
          isSearchLoading: false,
          searchAbortController: null,
        });
      }
    }
  },

  clearSearch: () => {
    const { searchAbortController } = get();
    if (searchAbortController) {
      searchAbortController.abort();
    }
    set({
      searchTerm: "",
      searchResults: null,
      isSearchLoading: false,
      searchError: null,
      searchAbortController: null,
    });
  },

  abortSearch: () => {
    const { searchAbortController } = get();
    if (searchAbortController) {
      searchAbortController.abort();
      set({
        isSearchLoading: false,
        searchAbortController: null,
      });
    }
  },
});
